import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, X, Loader2, CheckCircle2, AlertTriangle, Lock, BadgePercent } from "lucide-react";
import { trackPixel } from "@/lib/pixel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Survives React StrictMode's dev-only remount after the URL is cleaned.
let capturedSessionId = null;

export const MEMBER_PRICE = 279;
export const STUDENT_PRICE = Math.round(MEMBER_PRICE * 0.9);
const PACKAGE_COPY = {
  member: { name: "Membership", price: `RM${MEMBER_PRICE}`, amount: MEMBER_PRICE, note: `RM${MEMBER_PRICE}/month, auto-renews monthly · monthly 1:1 session, JANUS scan + Skin Passport update included · cancel anytime with one month's notice` },
};
export const OPENING_DATE_LABEL = "10 October 2026";
const OPENING_CHARGE_MS = Date.parse("2026-10-10T10:00:00+08:00");
export const PRE_OPENING = Date.now() < OPENING_CHARGE_MS - 49 * 3600 * 1000;

export function JoinButton({ packageId = "member", children, className = "", testId, defaultEmail = "", trial = null }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail);
  const [student, setStudent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const copy = PACKAGE_COPY[packageId] || PACKAGE_COPY.member;

  // Re-sync when the parent learns the email later (e.g. quiz gate unlock).
  useEffect(() => { if (defaultEmail) setEmail(defaultEmail); }, [defaultEmail]);

  const startCheckout = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          package_id: "member",
          email: email.trim(),
          origin_url: window.location.origin,
          cancel_path: window.location.pathname,
          student: student && !trial,
          ...(trial?.token ? { trial_token: trial.token } : {}),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Could not start checkout. Please try again.");
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch (err) {
      console.error("Checkout start failed:", err);
      setError(err.message);
      setBusy(false);
    }
  };

  const shownPrice = student && !trial ? `RM${STUDENT_PRICE}` : copy.price;

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className} data-testid={testId}>
        {children}
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/60 backdrop-blur-sm p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="checkout-modal-overlay"
        >
          <div
            className="w-full max-w-sm bg-cream border-[2.5px] border-ink rounded-2xl shadow-[6px_6px_0_#3D2B24] p-6 text-left"
            onClick={(e) => e.stopPropagation()}
            data-testid="checkout-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[0.66rem] font-bold uppercase tracking-[0.16em] text-mutedink mb-1">{copy.name}</div>
                <div className="font-disp font-extrabold text-3xl leading-none text-ink" data-testid="checkout-price">
                  {shownPrice}<span className="font-sans font-semibold text-sm text-mutedink"> /month</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-full border-2 border-ink hover:bg-sage transition-colors"
                aria-label="Close"
                data-testid="checkout-modal-close"
              >
                <X size={15} className="text-ink" strokeWidth={2.5} />
              </button>
            </div>
            <p className="text-sm text-mutedink mb-4">{copy.note}</p>
            {trial && (
              <div className="flex items-start gap-2 bg-sage border-2 border-ink rounded-xl px-3.5 py-2.5 mb-4" data-testid="checkout-trial-note">
                <BadgePercent size={15} className="flex-none mt-0.5 text-ink" strokeWidth={2.4} />
                <p className="text-xs text-ink leading-relaxed">
                  <strong className="font-semibold">First-visit credit applied:</strong> month 1 is RM0, covered by what you paid for your first visit.
                </p>
              </div>
            )}
            {PRE_OPENING && !trial && (
              <div className="flex items-start gap-2 bg-sage border-2 border-ink rounded-xl px-3.5 py-2.5 mb-4" data-testid="checkout-preopen-note">
                <CheckCircle2 size={15} className="flex-none mt-0.5 text-ink" strokeWidth={2.4} />
                <p className="text-xs text-ink leading-relaxed">
                  <strong className="font-semibold">You won't be charged until we open.</strong> Your card is saved today, the first charge lands on {OPENING_DATE_LABEL}, opening day.
                </p>
              </div>
            )}

            <form onSubmit={startCheckout}>
              <label className="block text-xs font-bold uppercase tracking-[0.12em] text-mutedink mb-1.5" htmlFor="checkout-email">
                Your email
              </label>
              <input
                id="checkout-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-paper border-2 border-ink rounded-xl px-4 py-3 text-ink placeholder:text-mutedink/60 focus:outline-none focus:ring-2 focus:ring-coral mb-3"
                data-testid="checkout-email-input"
              />
              {!trial && (
                <label className="flex items-start gap-2.5 text-sm text-ink mb-3 cursor-pointer select-none" data-testid="checkout-student-label">
                  <input
                    type="checkbox"
                    checked={student}
                    onChange={(e) => setStudent(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#B4552D]"
                    data-testid="checkout-student-checkbox"
                  />
                  <span>I'm a student, <strong className="font-semibold">10% off (RM{STUDENT_PRICE}/mo)</strong>. Show your student ID at your first visit.</span>
                </label>
              )}
              {error && (
                <p className="text-sm text-coral font-semibold mb-3" data-testid="checkout-error">{error}</p>
              )}
              <button
                type="submit"
                disabled={busy}
                className="group w-full inline-flex items-center justify-center gap-2 bg-clay text-white font-semibold px-6 py-3.5 rounded-full border-2 border-ink shadow-[4px_4px_0_#3D2B24] hover:-translate-y-[2px] active:translate-y-0 active:shadow-none transition-all disabled:opacity-60 disabled:cursor-wait"
                data-testid="checkout-pay-btn"
              >
                {busy ? <Loader2 size={17} className="animate-spin" /> : <>Subscribe, {shownPrice}/mo <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></>}
              </button>
            </form>
            <p className="flex items-center gap-1.5 text-xs text-mutedink mt-3">
              <Lock size={12} strokeWidth={2.5} /> Secure checkout by Stripe. You'll be redirected to pay.
            </p>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export function MembershipCTA({ className = "", testId, defaultEmail = "" }) {
  return (
    <JoinButton className={className} testId={testId} defaultEmail={defaultEmail}>
      Become a Member, RM{MEMBER_PRICE}/mo
      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
    </JoinButton>
  );
}

// Reads ?trial= from the URL and validates the first-visit credit token.
export function useTrialOffer() {
  const [trial, setTrial] = useState(null);
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("trial");
    if (!token) return;
    fetch(`${BACKEND_URL}/api/trial-offer/${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setTrial({ ...d, token }); })
      .catch((err) => console.error("Trial offer check failed:", err));
  }, []);
  return trial;
}

export function TrialCreditBanner({ trial }) {
  if (!trial) return null;
  const base = "flex items-start gap-3 text-left border-[2.5px] border-ink rounded-2xl px-5 py-4 mb-8 shadow-[4px_4px_0_#3D2B24]";
  if (trial.valid) {
    const until = new Date(trial.expires_at).toLocaleDateString("en-MY", { weekday: "long", day: "numeric", month: "long" });
    return (
      <div className={`${base} bg-sage`} data-testid="trial-credit-banner">
        <BadgePercent size={20} className="flex-none mt-0.5 text-ink" strokeWidth={2.4} />
        <p className="text-ink leading-relaxed">
          <strong className="font-semibold">Your first-visit credit is active.</strong>{" "}
          Join below and month 1 is RM0. Valid until {until}.
        </p>
      </div>
    );
  }
  const message =
    trial.reason === "used"
      ? "This first-visit credit has already been used."
      : trial.reason === "expired"
        ? "This first-visit credit has expired, it's valid for 7 days after your session. You can still join at the regular rate below."
        : "This credit link isn't valid. You can still join at the regular rate below.";
  return (
    <div className={`${base} bg-cream`} data-testid="trial-credit-banner">
      <AlertTriangle size={20} className="flex-none mt-0.5 text-coral" strokeWidth={2.4} />
      <p className="text-ink leading-relaxed">{message}</p>
    </div>
  );
}

export function PaymentReturnBanner() {
  const [state, setState] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id") || capturedSessionId;
    if (!sessionId) return;
    capturedSessionId = sessionId;
    window.history.replaceState({}, "", window.location.pathname);
    setState("checking");
    let attempts = 0;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      if (attempts >= 8) { setState("timeout"); return; }
      attempts += 1;
      try {
        const res = await fetch(`${BACKEND_URL}/api/payments/status/${sessionId}`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (data.payment_status === "paid") {
          setState("paid");
          trackPixel("Purchase", {
            value: (data.amount_total || 0) / 100,
            currency: (data.currency || "myr").toUpperCase(),
          });
          return;
        }
        if (data.status === "expired") { setState("expired"); return; }
        setTimeout(poll, 2000);
      } catch (err) {
        console.error("Payment status check failed:", err);
        setState("error");
      }
    };
    poll();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; reads session_id from the URL, all other refs are stable module imports/globals
  }, []);

  if (!state) return null;

  const base = "flex items-start gap-3 text-left border-[2.5px] border-ink rounded-2xl px-5 py-4 mb-8 shadow-[4px_4px_0_#3D2B24]";

  if (state === "paid") {
    return (
      <div className={`${base} bg-sage`} data-testid="payment-status-banner">
        <CheckCircle2 size={20} className="flex-none mt-0.5 text-ink" strokeWidth={2.4} />
        <p className="text-ink leading-relaxed">
          <strong className="font-semibold">Payment received, welcome to saebit.</strong>{" "}
          Your membership is active: RM{MEMBER_PRICE}/month, auto-renewed. We've saved your spot and will email you the details.
        </p>
      </div>
    );
  }
  if (state === "checking") {
    return (
      <div className={`${base} bg-paper`} data-testid="payment-status-banner">
        <Loader2 size={20} className="flex-none mt-0.5 text-ink animate-spin" />
        <p className="text-ink leading-relaxed">Confirming your payment with Stripe…</p>
      </div>
    );
  }
  const message =
    state === "expired"
      ? "That checkout session expired before payment was completed. No charge was made, you can try again below."
      : state === "timeout"
        ? "We couldn't confirm the payment yet. If you completed checkout, you'll receive a confirmation shortly, no need to pay again."
        : "We hit a snag checking your payment status. If you completed checkout, you won't be charged twice, please check back shortly.";
  return (
    <div className={`${base} bg-cream`} data-testid="payment-status-banner">
      <AlertTriangle size={20} className="flex-none mt-0.5 text-coral" strokeWidth={2.4} />
      <p className="text-ink leading-relaxed">{message}</p>
    </div>
  );
}
